import type { Language } from '@hero/schema';

/**
 * Append (or replace) the `lang` query parameter on a Moyasar hosted
 * checkout URL so the page renders in the user's current language.
 *
 * Moyasar supports `ar` and `en`. Arabic users get `?lang=ar`; every
 * other language — including Urdu (which Moyasar doesn't support) and
 * any unknown locale — falls back to `?lang=en` so non-Arabic users
 * are never dropped into an Arabic-only page.
 *
 * Used everywhere the frontend hands a Moyasar checkout URL off to
 * the browser — `useBillingPayManager.payBilling`,
 * `useSubscriptionPayManager`, inline "Pay now" buttons, and the
 * admin `BillingView` link. The backend stamps the raw URL onto
 * `Billing.checkout.url`; locale-flipping is a pure client concern
 * (it depends on the user's i18n language at click time, not on the
 * server's notion of an org's default language).
 *
 * Malformed input is returned unchanged — this helper is called from
 * UI paths and must never throw.
 */
export function localizeMoyasarCheckoutUrl(
  url: string,
  lang: Language | string | undefined,
): string {
  const moyasarLang: 'ar' | 'en' = lang === 'ar' ? 'ar' : 'en';
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('lang', moyasarLang);
    return parsed.toString();
  } catch {
    return url;
  }
}
